/**
 * useSubscryptsContract - Hook for interacting with the Subscrypts smart contract.
 *
 * Provides methods for:
 * - Reading plan and subscription data
 * - Creating subscriptions and plans
 * - Token operations (balances, approvals)
 */

import { useCallback, useMemo } from 'react';
import { BrowserProvider, formatUnits, parseUnits, type TransactionResponse, type TransactionReceipt } from 'ethers';
import { useWeb3 } from '../contexts/Web3Context';

export interface Plan {
  id: bigint;
  merchantAddress: string;
  currencyCode: bigint;
  subscriptionAmount: bigint;
  paymentFrequency: bigint;
  referralBonus: bigint;
  commission: bigint;
  description: string;
  defaultAttributes: string;
  verificationExpiryDate: bigint;
  isActive: boolean;
  subscriberCount: bigint;
}

export interface Subscription {
  id: bigint;
  planId: bigint;
  subscriber: string;
  referral: string;
  recurring: boolean;
  remainingCycles: bigint;
  lastPaymentDate: bigint;
  expiryDate: bigint;
  attribute: string;
  isActive: boolean;
}

export const useSubscryptsContract = () => {
  const { getContract, getAbi, contractAddress, signer, isConnected, abis } = useWeb3();

  const subsReadOnly = useMemo(() => {
    if (!abis.Subscrypts) return null;
    try {
      return getContract(contractAddress, getAbi('Subscrypts'), 'readOnly');
    } catch {
      return null;
    }
  }, [getContract, getAbi, contractAddress, abis]);

  const subsSigner = useMemo(() => {
    if (!abis.Subscrypts || !signer) return null;
    try {
      return getContract(contractAddress, getAbi('Subscrypts'), 'signer');
    } catch {
      return null;
    }
  }, [getContract, getAbi, contractAddress, signer, abis]);

  const waitForReceipt = useCallback(async (tx: TransactionResponse): Promise<TransactionReceipt> => {
    try {
      const receipt = await tx.wait();
      if (!receipt) throw new Error('Transaction failed - no receipt');
      return receipt;
    } catch (err: any) {
      // MetaMask network switch during tx (e.g., mainnet -> Arbitrum) causes NETWORK_ERROR
      if (err?.code === 'NETWORK_ERROR' && err?.event === 'changed') {
        console.warn('[waitForReceipt] Network changed during tx.wait(), retrying with fresh provider...');
        const ethereum = (window as any).ethereum;
        if (!ethereum) throw new Error('No wallet provider available for retry');
        const freshProvider = new BrowserProvider(ethereum);
        const receipt = await freshProvider.waitForTransaction(tx.hash);
        if (!receipt) throw new Error('Transaction failed - no receipt after network retry');
        return receipt;
      }
      throw err;
    }
  }, []);

  // READ METHODS

  const getPlan = useCallback(async (planId: number | bigint): Promise<Plan | null> => {
    if (!subsReadOnly) return null;
    try {
      return await subsReadOnly.getPlan(planId);
    } catch (e) {
      console.error('Failed to get plan:', e);
      return null;
    }
  }, [subsReadOnly]);

  const getSubscription = useCallback(async (subscriptionId: number | bigint): Promise<Subscription | null> => {
    if (!subsReadOnly) return null;
    try {
      return await subsReadOnly.getSubscription(subscriptionId);
    } catch (e) {
      console.error('Failed to get subscription:', e);
      return null;
    }
  }, [subsReadOnly]);

  const getPlanSubscription = useCallback(async (planId: number | bigint, subscriber: string): Promise<Subscription | null> => {
    if (!subsReadOnly) return null;
    try {
      return await subsReadOnly.getPlanSubscription(planId, subscriber);
    } catch (e) {
      console.error('Failed to get plan subscription:', e);
      return null;
    }
  }, [subsReadOnly]);

  const balanceOf = useCallback(async (address: string): Promise<bigint> => {
    if (!subsReadOnly) return 0n;
    try {
      return await subsReadOnly.balanceOf(address);
    } catch (e) {
      console.error('Failed to get balance:', e);
      return 0n;
    }
  }, [subsReadOnly]);

  const allowance = useCallback(async (owner: string, spender: string): Promise<bigint> => {
    if (!subsReadOnly) return 0n;
    try {
      return await subsReadOnly.allowance(owner, spender);
    } catch (e) {
      console.error('Failed to get allowance:', e);
      return 0n;
    }
  }, [subsReadOnly]);

  const planCreationCost = useCallback(async (): Promise<bigint> => {
    if (!subsReadOnly) return 0n;
    try {
      return await subsReadOnly.planCreationCost();
    } catch (e) {
      console.error('Failed to get plan creation cost:', e);
      return 0n;
    }
  }, [subsReadOnly]);

  // WRITE METHODS

  const subscriptionCreate = useCallback(async (
    planId: number | bigint,
    subscriber: string,
    recurring: boolean,
    remainingCycles: number,
    referral: string,
    onlyCreate: boolean,
    deductFrom: string
  ): Promise<TransactionReceipt | null> => {
    if (!subsSigner) throw new Error('Wallet not connected');
    const tx = await subsSigner.subscriptionCreate(
      planId, subscriber, recurring, remainingCycles, referral, onlyCreate, deductFrom
    );
    return await waitForReceipt(tx);
  }, [subsSigner, waitForReceipt]);

  const planCreate = useCallback(async (
    currency: number,
    amount: bigint,
    frequency: bigint,
    description: string,
    attribute: string,
    referralBonus: bigint
  ): Promise<{ receipt: TransactionReceipt; planId?: bigint }> => {
    if (!subsSigner || !subsReadOnly) throw new Error('Wallet not connected');
    const fee = await subsReadOnly.planCreationCost();
    const tx = await subsSigner.planCreate(
      currency, amount, frequency, description, attribute, referralBonus, { value: fee }
    );
    const receipt = await waitForReceipt(tx);

    let planId: bigint | undefined;
    for (const log of receipt.logs) {
      try {
        const parsed = subsSigner.interface.parseLog({
          topics: log.topics as string[],
          data: log.data
        });
        if (parsed?.name === '_planCreate' || parsed?.name === 'PlanCreated') {
          planId = parsed.args[0] as bigint;
          break;
        }
      } catch {
        // Not our event
      }
    }
    return { receipt, planId };
  }, [subsSigner, subsReadOnly, waitForReceipt]);

  const approve = useCallback(async (spender: string, amount: bigint): Promise<TransactionReceipt | null> => {
    if (!subsSigner) throw new Error('Wallet not connected');
    const tx = await subsSigner.approve(spender, amount);
    return await waitForReceipt(tx);
  }, [subsSigner, waitForReceipt]);

  // DEX / PRICE METHODS

  /**
   * Convert SUBS amount to USD value
   */
  const convertTokenToOtherCurrency = useCallback(async (amount: bigint): Promise<bigint> => {
    if (!subsReadOnly) return 0n;
    try {
      const result = await subsReadOnly.convertTokenToOtherCurrency(amount);
      // Contract returns [outputUSDC, inputSUBS, rate]
      return result[0];
    } catch (e: any) {
      const msg = e?.reason || e?.message || '';
      if (msg.includes('too small') || msg.includes('CALL_EXCEPTION')) {
        console.debug('USD conversion unavailable (low liquidity):', msg);
      } else {
        console.error('Failed to convert token to USD:', e);
      }
      return 0n;
    }
  }, [subsReadOnly]);

  /**
   * Get DEX addresses (router, quoter, USDC, pair)
   */
  const getDexAddresses = useCallback(async () => {
    if (!subsReadOnly) return null;
    try {
      const [router, quoter, usdc, pair] = await Promise.all([
        subsReadOnly.dexRouter(),
        subsReadOnly.dexQuoter(),
        subsReadOnly.dexUSDC(),
        subsReadOnly.dexPair()
      ]);
      return { router, quoter, usdc, pair };
    } catch (e) {
      console.error('Failed to get DEX addresses:', e);
      return null;
    }
  }, [subsReadOnly]);

  /**
   * Quote USDC amount to SUBS amount using Uniswap V3 Quoter.
   *
   * @param amountUsdcWad Amount in USDC (18 decimals)
   * @returns Amount in SUBS (18 decimals)
   */
  const quoteUsdcToSubs = useCallback(async (amountUsdcWad: bigint): Promise<bigint> => {
    if (!subsReadOnly) return 0n;

    try {
      const quoter = await subsReadOnly.dexQuoter();
      const usdc = await subsReadOnly.dexUSDC();

      if (!quoter || !usdc) return 0n;

      // Adjust decimals (18 -> 6): USDC on Arbitrum is 6 decimals
      const amountUsdc6 = amountUsdcWad / 1000000000000n;
      if (amountUsdc6 === 0n) return 0n;

      // Get Quoter contract
      const quoterAbi = getAbi('dexQuoterABI');
      const quoterContract = getContract(quoter, quoterAbi, 'readOnly');

      // Try common fee tiers: 3000 (0.3%), 10000 (1%), 500 (0.05%)
      const feeTiers = [3000, 10000, 500];

      for (const fee of feeTiers) {
        try {
          const amountOut = await quoterContract.quoteExactInputSingle.staticCall(
            usdc,
            contractAddress,
            fee,
            amountUsdc6,
            0
          );
          if (amountOut > 0n) return amountOut;
        } catch {
          // Continue to next fee tier
        }
      }
    } catch (e) {
      console.error('Failed to quote USDC to SUBS:', e);
    }
    return 0n;
  }, [subsReadOnly, getAbi, getContract, contractAddress]);

  // UTILITY METHODS

  const formatTokenAmount = useCallback((amount: bigint | null | undefined, decimals: number = 18): string => {
    if (amount === null || amount === undefined) return '0';
    return formatUnits(amount, decimals);
  }, []);

  const parseTokenAmount = useCallback((amount: string, decimals: number = 18): bigint => {
    return parseUnits(amount, decimals);
  }, []);

  const formatDuration = useCallback((seconds: bigint): string => {
    const sec = Number(seconds);
    if (sec >= 31536000) return `${Math.floor(sec / 31536000)} year(s)`;
    if (sec >= 2592000) return `${Math.floor(sec / 2592000)} month(s)`;
    if (sec >= 604800) return `${Math.floor(sec / 604800)} week(s)`;
    if (sec >= 86400) return `${Math.floor(sec / 86400)} day(s)`;
    if (sec >= 3600) return `${Math.floor(sec / 3600)} hour(s)`;
    return `${sec} second(s)`;
  }, []);

  return {
    contractReadOnly: subsReadOnly,
    contractSigner: subsSigner,
    isReady: !!subsReadOnly,
    canWrite: !!subsSigner && isConnected,
    getPlan, getSubscription, getPlanSubscription,
    balanceOf, allowance, planCreationCost,
    convertTokenToOtherCurrency, getDexAddresses, quoteUsdcToSubs,
    subscriptionCreate, planCreate, approve,
    formatTokenAmount, parseTokenAmount, formatDuration,
    waitForReceipt
  };
};

export default useSubscryptsContract;
